import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/MessagesService';
import { Message } from '../types';

export const useMessage = (messageId?: string) => {
    const queryClient = useQueryClient();

    const messageQuery = useQuery({
        queryKey: ['message', messageId],
        queryFn: () => {
            if (!messageId) throw new Error('messageId is required');
            return fetch(messageId);
        },
        enabled: !!messageId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: Message) => {
            const { created, updated, tags, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages'] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (record: Message) => {
            if (!messageId) throw new Error('messageId is required');
            return update(messageId, record);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages'] });
            queryClient.invalidateQueries({ queryKey: ['message', messageId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!messageId) throw new Error('messageId is required');
            return deleteRecord(messageId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages'] });
        }
    });

    const fetchRecord = () => {
        messageQuery.refetch();
    };

    const createRecord = (values: Message) => {
        createMutation.mutate(values);
    };

    const updateRecord = (values: Message) => {
        updateMutation.mutate(values);
    };

    const deleteRecordAction = () => {
        deleteMutation.mutate();
    };

    return {
        record: messageQuery.data,
        loading: messageQuery.isLoading,
        fetchRecord,
        createRecord,
        updateRecord,
        deleteRecord: deleteRecordAction
    };
};
